import { useCallback, useEffect } from "react";
import {
  Control,
  Controller,
  FieldValues,
  FormProvider,
  Path,
  RegisterOptions,
  useForm,
  UseFormRegister
} from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { CloseIcon } from "@/assets/icons";
import BadgeDataType from "@/components/ui/badge-data-type";
import Dropdown from "@/components/ui/dropdown";
import { IOptionItem } from "@/components/ui/dropdown/types";
import FormField from "@/components/ui/formField";
import RadioButton from "@/components/ui/radio";
import { MESSAGE } from "@/constants/message";
import { GEOHASH_REGEX } from "@/constants/regex";
import { useMap } from "@/hooks/map/core/useMapContext";
import useSessionStorage from "@/hooks/store/useSessionStorage";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import { RootState } from "@/store";
import { clearDrawPolygonDataset } from "@/store/slice/spatialSlice";
import {
  enabledCustomDrawPolygonDataset,
  enableDrawPolygon,
  resetShowPanelDatasetDetail
} from "@/store/slice/uiSlice";
import { SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetSchemaLabel
} from "@/types/dataset";
import { convertFormatterDayjs } from "@/utils/date";

import { Button } from "equator-design-system";
import { isValidCell } from "h3-js";

import { DatasetDetailRow } from "../columns";
import {
  DRAW_POLYGON_INPUT,
  GEOJSON_INPUT,
  LATITUDE_INPUT,
  LONGITUDE_INPUT,
  WKT_INPUT
} from "./constant";
import EFormatGeom, { GeometryForm } from "./GeometryForm";

interface FormActionPanelProps {
  fields: DatasetAttributeSchema[];
  initialValues?: DatasetDetailRow | null;
  onSubmit: (
    data: Record<string, unknown>,
    originalData?: DatasetDetailRow | null
  ) => void;
  spatialAggregationType?: SpatialAggregationType | null;
}

interface FieldDatasetsProps<T extends FieldValues> {
  type: DataSchemaType;
  name: Path<T>;
  register: UseFormRegister<T>;
  rules: RegisterOptions<T>;
  error?: string;
  control: Control<T>;
}

const BadgeIcon = ({ type }: { type: DataSchemaType }) => {
  return <BadgeDataType type={type} />;
};

export interface DatasetDetailSessionStorageProps {
  id?: string;
  dataFields?: DatasetAttributeSchema[];
  data?: Record<string, unknown>;
  type?: "add" | "edit";
  image?: Record<string, string>;
  originalData?: DatasetDetailRow | null;
  name?: string;
}

const FieldDatasets = <T extends FieldValues>({
  type,
  name,
  register,
  rules,
  error,
  control
}: FieldDatasetsProps<T>) => {
  switch (type) {
    case DataSchemaType.BOOL: {
      const booleanOption: IOptionItem[] = [
        { label: "True", value: "true" },
        { label: "False", value: "false" }
      ];

      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field: { onChange, value, onBlur } }) => (
            <Dropdown
              className="text-sm"
              value={
                booleanOption?.find((c) => c.value === String(value)) || null
              }
              aria-label={name}
              data={booleanOption}
              placeholder={name}
              labelBorder=""
              onSelect={(val) => {
                onChange(val?.value ?? "");
                onBlur();
              }}
              suffix={<BadgeIcon type={type} />}
            />
          )}
        />
      );
    }
    case DataSchemaType.DATE:
      return (
        <FormField
          name={name}
          register={register}
          placeHolder={name}
          suffix={<BadgeIcon type={type} />}
          rules={rules}
          error={error}
          type="date"
        />
      );
    case DataSchemaType.TIME:
      return (
        <FormField
          name={name}
          register={register}
          placeHolder={name}
          suffix={<BadgeIcon type={type} />}
          rules={rules}
          error={error}
          type="time"
        />
      );
    case DataSchemaType.DATE_TIME:
      return (
        <FormField
          name={name}
          register={register}
          placeHolder={name}
          suffix={<BadgeIcon type={type} />}
          rules={rules}
          error={error}
          type="datetime-local"
        />
      );
    default:
      return (
        <FormField
          name={name}
          register={register}
          placeHolder={name}
          suffix={<BadgeIcon type={type} />}
          rules={rules}
          error={error}
        />
      );
  }
};

export const FormActionPanel = ({
  fields,
  initialValues,
  onSubmit,
  spatialAggregationType
}: FormActionPanelProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    sessionValue: sessionStorageData,
    setSessionItem: setItem,
    removeSessionItem: removeItem
  } = useSessionStorage(
    "dataset-detail-draw-polygon",
    {} as DatasetDetailSessionStorageProps
  );
  const existingFields: DatasetAttributeSchema[] =
    sessionStorageData?.dataFields ?? fields;

  const { drawnPolygon } = useSelector((state: RootState) => state.spatial);

  const { showConfirmation } = useConfirmationModal();
  const { enterDrawMode } = useMap();

  // Helper function to get geometry-related session data
  const getGeomSession = (attr: string, session: Record<string, unknown>) => {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(session)) {
      if (!value) continue;

      const names = key.split("_");

      if (names.length >= 3 && names.at(-1) === attr) {
        result[key] = value;
      }
    }

    return result;
  };

  const methods = useForm({
    mode: "onChange",
    defaultValues: existingFields.reduce(
      (acc, field) => {
        const rawValue =
          sessionStorageData?.data?.[field.attribute_name] ??
          initialValues?.[field.attribute_name];

        const safeBooleanValue =
          typeof rawValue === "boolean" ? String(rawValue) : rawValue;
        if (safeBooleanValue === undefined || safeBooleanValue === null) {
          acc[field.attribute_name] = "";
          return acc;
        }

        if (field.data_type === DataSchemaType.DATE) {
          acc[field.attribute_name] = convertFormatterDayjs(
            safeBooleanValue as string,
            "YYYY-MM-DD"
          );
        } else if (field.data_type === DataSchemaType.DATE_TIME) {
          // input type="datetime-local" => YYYY-MM-DDTHH:mm
          acc[field.attribute_name] = convertFormatterDayjs(
            safeBooleanValue as string,
            "YYYY-MM-DDTHH:mm"
          );
        } else if (field.data_type === DataSchemaType.TIME) {
          // input type="time" => HH:mm
          acc[field.attribute_name] = convertFormatterDayjs(
            safeBooleanValue as string,
            "HH:mm"
          );
        } else if (field.data_type === DataSchemaType.GEOMETRY) {
          const geomSession = getGeomSession(
            field.attribute_name,
            sessionStorageData?.data || {}
          );
          const formatGeom =
            Object.keys(geomSession).length || safeBooleanValue;

          if (Object.keys(geomSession).length) {
            const geomEntries = Object.entries(geomSession);
            for (const [key, value] of geomEntries) {
              acc[key] = value;
            }
          } else {
            acc[`wkt_geom_input_${field.attribute_name}`] = safeBooleanValue;
          }

          acc[field.attribute_name] = safeBooleanValue;
          acc[`format_geom_${field.attribute_name}`] = formatGeom
            ? sessionStorageData?.data?.[
                `format_geom_${field.attribute_name}`
              ] || "wkt"
            : "";
        } else {
          acc[field.attribute_name] = safeBooleanValue;
        }

        return acc;
      },
      {} as Record<string, unknown>
    )
  });

  const {
    register,
    control,
    formState: { isValid, errors, isDirty, dirtyFields },
    handleSubmit,
    watch,
    getValues,
    setValue,
    resetField,
    reset
  } = methods;

  const handleConfirmClose = useCallback(() => {
    removeItem();
    dispatch(resetShowPanelDatasetDetail());
    dispatch(clearDrawPolygonDataset());
  }, [dispatch, removeItem]);

  const generatePattern = (dataType: DataSchemaType) => {
    switch (dataType) {
      case DataSchemaType.INT:
        return {
          pattern: /^[+-]?\d+$/,
          message: "Only numeric values are allowed"
        };
      case DataSchemaType.FLOAT:
      case DataSchemaType.LATITUDE:
      case DataSchemaType.LONGITUDE:
      case DataSchemaType.PERCENTAGE:
      case DataSchemaType.RATIO:
        return {
          pattern: /^[+-]?(\d*[.])?\d+$/,
          message: "Only numeric values are allowed"
        };
      case DataSchemaType.BOOL:
        return { pattern: /^(true|false)$/i, message: "" };
      default:
        return { pattern: /.*/, message: "" };
    }
  };

  const generateValidate = (value: string, dataLabel?: DatasetSchemaLabel) => {
    if (dataLabel === DatasetSchemaLabel.H3 && !isValidCell(value)) {
      return "Invalid H3 index. Please provide a valid H3 index.";
    }
    if (
      dataLabel === DatasetSchemaLabel.GEOHASH &&
      !GEOHASH_REGEX.test(value)
    ) {
      return "Invalid Geohash. Please provide a valid Geohash.";
    }
    return true;
  };
  const titlePanel =
    initialValues || sessionStorageData?.type === "edit"
      ? "Edit Row"
      : "Add New Row";
  const submitButton =
    initialValues || sessionStorageData?.type === "edit" ? "Update" : "Create";
  const isEdit = !!initialValues || sessionStorageData?.type === "edit";
  const disableSubmit = !isValid || (isEdit && !isDirty);

  const handleClose = useCallback(() => {
    if (Object.keys(dirtyFields).length) {
      showConfirmation({
        title: "Unsaved Changes",
        message: MESSAGE.CONFIRMATION_LEAVE,
        cancelLabel: "Cancel",
        confirmLabel: "Leave",
        onConfirm: handleConfirmClose
      });
    } else handleConfirmClose();
  }, [showConfirmation, dirtyFields, handleConfirmClose]);

  const fieldsWithoutGeometry = fields.filter(
    (field) => field.data_type !== DataSchemaType.GEOMETRY
  );

  const onClearGeomValue = (type: EFormatGeom, name: string) => {
    const removeItems = Object.values(EFormatGeom).filter(
      (value) => value !== type
    );
    for (const item of removeItems) {
      if (item === EFormatGeom.COORDINATES) {
        setValue(`${LATITUDE_INPUT}${name}`, "");
        setValue(`${LONGITUDE_INPUT}${name}`, "");
      } else if (item === EFormatGeom.WKT) {
        setValue(`${WKT_INPUT}${name}`, "");
      } else if (item === EFormatGeom.GEOJSON) {
        setValue(`${GEOJSON_INPUT}${name}`, "");
      } else if (item === EFormatGeom.DRAW_POLYGON) {
        const newImage = { ...sessionStorageData?.image };
        if (newImage && name in newImage) {
          delete newImage[name];
        }
        setItem({
          ...sessionStorageData,
          image: newImage
        });
        setValue(`${DRAW_POLYGON_INPUT}${name}`, "");
        dispatch(clearDrawPolygonDataset(name));
      }
    }
  };

  const onDeleteDraw = useCallback(
    (name: string) => {
      const newImage = { ...sessionStorageData?.image };
      if (newImage && name in newImage) {
        delete newImage[name];
      }
      setItem({
        ...sessionStorageData,
        image: newImage
      });
      dispatch(clearDrawPolygonDataset(name));
      resetField(sessionStorageData?.name || "");
    },
    [dispatch, resetField, sessionStorageData, setItem]
  );

  useEffect(() => {
    // To show a confirmation dialog when the user tries to leave the page
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      handleConfirmClose();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleConfirmClose]);

  useEffect(() => {
    if (!drawnPolygon) return;

    for (const [name, polygon] of Object.entries(drawnPolygon)) {
      const key = `draw_polygon_${name}`;
      const value = JSON.stringify(polygon.geometry);

      setValue(key, value, { shouldDirty: true, shouldTouch: true });
    }
  }, [drawnPolygon, setValue, register, getValues, reset]);

  const handleDraw = useCallback(
    (name: string) => {
      // Implement the logic for drawing a polygon here
      dispatch(enableDrawPolygon());
      dispatch(enabledCustomDrawPolygonDataset());
      enterDrawMode();

      setItem({
        data: getValues(),
        dataFields: fields,
        type: isEdit ? "edit" : "add",
        image: sessionStorageData?.image,
        originalData: initialValues || sessionStorageData?.originalData,
        name: name,
        id: id
      });
      navigate("/");
    },
    [
      dispatch,
      enterDrawMode,
      fields,
      getValues,
      id,
      initialValues,
      isEdit,
      navigate,
      sessionStorageData,
      setItem
    ]
  );

  const originalData = initialValues ?? sessionStorageData?.originalData;

  return (
    <div className="absolute top-4 right-4 z-[9] border-gainsboro bg-white rounded-lg w-[456px] h-[calc(100vh-32px)] overflow-y-auto flex flex-col">
      <div className="px-[24px] py-4">
        <div className="flex flex-row items-center justify-between">
          <h2 className="text-txtHeading font-sans font-bold text-[20px]">
            {titlePanel}
          </h2>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="p-0 bg-transparent border-0"
              aria-label={`Close ${titlePanel} Panel`}
            >
              <CloseIcon className="fill-dim-gray" />
            </button>
          </div>
        </div>
      </div>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit((data) => onSubmit(data, originalData))}
          className="flex flex-col flex-1"
        >
          <div className="flex flex-col flex-1 gap-4 px-4">
            {fieldsWithoutGeometry.map((field) => (
              <FieldDatasets
                name={field.attribute_name}
                key={field.id}
                register={register}
                rules={{
                  required: true,
                  pattern: {
                    value: generatePattern(field.data_type).pattern,
                    message: generatePattern(field.data_type).message
                  },
                  validate: (value) =>
                    generateValidate(value as string, field?.data_label)
                }}
                error={errors[field.attribute_name]?.message}
                type={field.data_type}
                control={control}
              />
            ))}
            {fields
              .filter((field) => field.data_type === DataSchemaType.GEOMETRY)
              .map((field) => (
                <GeometryForm
                  key={field.attribute_name}
                  formatGeom={
                    watch(`format_geom_${field.attribute_name}`) as EFormatGeom
                  }
                  register={register}
                  name={field.attribute_name}
                  control={control}
                  onDraw={() => handleDraw(field.attribute_name)}
                  mapImg={sessionStorageData?.image?.[field.attribute_name]}
                  drawPolygonState={drawnPolygon?.[field.attribute_name]}
                  onDeleteDraw={onDeleteDraw}
                  onClearGeomValue={onClearGeomValue}
                  type={field.data_label}
                />
              ))}
            {spatialAggregationType === "adm_area" && (
              <div className="flex flex-col gap-3">
                <Controller
                  name="admin_area_level"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <div className="flex flex-col gap-3 mt-4">
                      <p className="text-sm text-txtHeading font-semibold">
                        Attribute Selection
                      </p>
                      <span className="text-xs">
                        Please select one get the geometric value of the
                        administrative area.
                      </span>
                      {fieldsWithoutGeometry.map((item) => (
                        <RadioButton
                          key={item.attribute_name}
                          name={item.attribute_name}
                          checked={value === item.attribute_name}
                          onCheck={() => onChange(item.attribute_name)}
                          label={item.attribute_name}
                          inputClassName="w-5 h-5 border-dim-gray"
                          labelClassName="text-xs font-normal text-txtHeading"
                        />
                      ))}
                    </div>
                  )}
                />
              </div>
            )}
          </div>

          <div className="flex px-4 justify-center gap-2 bg-white w-full py-[20px] rounded-t-none rounded-lg">
            <Button
              className="h-[40px] w-[198px] btn-secondary text-sm font-bold"
              onClick={handleClose}
              type="button"
            >
              <span>Cancel</span>
            </Button>
            <Button
              className="h-[40px] w-[198px] btn-primary text-sm font-bold"
              disabled={disableSubmit}
              type="submit"
            >
              <span>{submitButton}</span>
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
